import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useCaptureError } from '@/lib/posthog/hooks/useCaptureError';
import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { useUpdateSignupFlowMutation } from '@/modules/signup-flow/mutations/useUpdateSignupFlowMutation';
import { signupFlowStep } from '@/modules/signup-flow/signupFlowSteps';
import { fitsInOnePage } from '@/modules/slack-notification-channel/channelPages';
import { useSetSlackNotificationChannelMutation } from '@/modules/slack-notification-channel/mutations/useSetSlackNotificationChannelMutation';
import { slackNotificationChannelQueryKeys } from '@/modules/slack-notification-channel/queries/queryKeys';
import { useSelectableSlackChannelsQuery } from '@/modules/slack-notification-channel/queries/useSelectableSlackChannelsQuery';
import { useIsSlackConnected } from '@/modules/user-external-accounts/hooks/useIsSlackConnected';
import { useUserExternalAccountsQuery } from '@/modules/user-external-accounts/queries/useUserExternalAccountsQuery';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

import { SlackChannelPicker } from './SlackChannelPicker';

interface SlackChannelStepProps {
  onBack: () => void;
}

/**
 * Signup must not wait on Slack: when the first page of channels takes longer,
 * rate-limit waits included, the step offers Continue (direct messages).
 */
const FIRST_PAGE_BUDGET_MS = 10_000;

/**
 * Slack step after OAuth: pick the channel Finlay posts to, or keep
 * direct messages. Either path persists the summary step.
 */
export const SlackChannelStep = ({ onBack }: SlackChannelStepProps) => {
  const captureError = useCaptureError();
  const trackEvent = useTrackEvent();

  const currentUserQuery = useCurrentUserQuery();
  const { userId } = useIsSlackConnected();
  const externalAccountsQuery = useUserExternalAccountsQuery(userId);
  const queryClient = useQueryClient();
  const channelsQuery = useSelectableSlackChannelsQuery({
    firstPageBudgetMs: FIRST_PAGE_BUDGET_MS,
    // Refetch on return from Slack so a private channel Finlay was just
    // invited to (see the picker's `/invite` hint) appears without a reload.
    // Only when the list is one page: refetching every page of a large
    // workspace on each focus would spend its Slack rate limit.
    refetchOnWindowFocus: (query) =>
      fitsInOnePage(query.state.data?.pages) ? 'always' : false,
  });
  // A large workspace refreshes on request instead, from page 1.
  const refreshChannels = () =>
    void queryClient.resetQueries({
      queryKey: slackNotificationChannelQueryKeys.availableChannelPages(),
    });
  const isMultiPage =
    channelsQuery.pages !== undefined && !fitsInOnePage(channelsQuery.pages);
  const setChannelMutation = useSetSlackNotificationChannelMutation();
  const updateSignupFlowMutation = useUpdateSignupFlowMutation();

  const workspaceName =
    externalAccountsQuery.data?.find(
      (account) => account.type === 'Slack' && account.status === 'Active',
    )?.teamName ?? 'your workspace';

  const finishStep = () => {
    updateSignupFlowMutation.mutate(
      { step: signupFlowStep.summary },
      {
        onError: (error) => {
          captureError(error, {
            errorType: 'slack-integration-signup-flow-error',
          });
          toast.error(
            'Failed to continue to the next step. Please try again or contact support.',
          );
        },
        onSuccess: () => {
          trackEvent('slackIntegrationFinished');
        },
      },
    );
  };

  const handleAddToChannel = (channelId: string) => {
    setChannelMutation.mutate(
      { channelId },
      {
        onSuccess: finishStep,
        onError: (error) => {
          toast.error(
            `Could not add Finlay to that channel: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  return (
    <SlackChannelPicker
      workspaceName={workspaceName}
      firstName={currentUserQuery.data?.firstName ?? undefined}
      channels={channelsQuery.channels}
      // Slack filters pages after fetching them, so early pages can be empty.
      isLoadingChannels={
        channelsQuery.isPending ||
        (channelsQuery.channels.length === 0 && channelsQuery.isLoadingMore)
      }
      hasChannelsError={channelsQuery.isError}
      isLoadingMore={channelsQuery.isLoadingMore}
      hasLoadMoreError={channelsQuery.hasLoadMoreError}
      onRetryLoadMore={channelsQuery.retry}
      onRefresh={isMultiPage ? refreshChannels : undefined}
      isSubmitting={
        setChannelMutation.isPending || updateSignupFlowMutation.isPending
      }
      onBack={onBack}
      onAddToChannel={handleAddToChannel}
      onSkip={finishStep}
    />
  );
};
